import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { UserPlus, Mail, Trash2, Users, Clock, User, X, DollarSign, FileSpreadsheet, ChevronDown, ChevronRight, FolderOpen } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Link, useLocation } from "wouter";
import { useSentResourceGrants } from "@/lib/queries/resource-shares";

export interface Assistant {
  id: string;

  userId: string;

  assistantType: string;

  hourlyRate: number | null;

  status: string;

  createdAt: string;
  permissions?: Partial<Record<AssistantCapability, boolean>>;

  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
}
export type AssistantCapability = "notes" | "goals" | "calendar" | "tasks" | "notepad" | "habits" | "trackerX" | "contacts" | "ai" | "mcp" | "retellInbound" | "retellOutbound" | "vault" | "media" | "planner" | "intentions" | "gratefuls" | "incantations" | "checklists" | "drive" | "meditation" | "analytics" | "vboard" | "sops" | "timeBudget" | "suggestions";
const ALL_CAPABILITIES: AssistantCapability[] = [
  "notes", "goals", "calendar", "tasks", "notepad", "habits", "trackerX",
  "contacts", "ai", "mcp", "retellInbound", "retellOutbound", "vault", "media",
  "planner", "intentions", "gratefuls", "incantations", "checklists",
  "drive", "meditation", "analytics", "vboard", "sops", "timeBudget", "suggestions",
];
function normalizePerms(input: Partial<Record<AssistantCapability, boolean>> | undefined): Record<AssistantCapability, boolean> {
  return Object.fromEntries(ALL_CAPABILITIES.map(k => [k, input?.[k] === true])) as Record<AssistantCapability, boolean>;
}
export const capabilityGroups: Array<{ title: string; items: Array<[AssistantCapability, string]> }> = [
  { title: "Core", items: [["goals", "Goals"], ["tasks", "Tasks"], ["calendar", "Calendar"], ["contacts", "Contacts"], ["notepad", "Notepad"], ["notes", "Journal"]] },
  { title: "Productivity", items: [["planner", "Daily Planner"], ["checklists", "Checklists"], ["timeBudget", "Time Budget"], ["sops", "SOPs"]] },
  { title: "Mindfulness", items: [["intentions", "Intentions"], ["gratefuls", "Gratitude"], ["incantations", "Incantations"], ["meditation", "Meditation"]] },
  { title: "Content & Drive", items: [["media", "Media Library"], ["vboard", "Vision Board"], ["drive", "Google Drive"]] },
  { title: "Insights", items: [["analytics", "Analytics"], ["suggestions", "Suggestions"]] },
  { title: "Privileged", items: [["ai", "AI"], ["mcp", "MCP / API keys"], ["retellInbound", "Retell inbound"], ["retellOutbound", "Retell outbound"], ["vault", "Personal Vault"]] },
];

export type AssistantRosterType = "assistants" | "coach";

export function filterAssistantRoster(
  assistants: Assistant[],
  rosterType: AssistantRosterType,
): Assistant[] {
  const seenUserIds = new Set<string>();
  return assistants.filter((assistant) => {
    if (assistant.status !== "active") return false;
    if (rosterType === "coach") {
      if (assistant.assistantType !== "coach" || seenUserIds.has(assistant.userId)) return false;
    } else if (
      (assistant.assistantType !== "assistant" && assistant.assistantType !== "virtual_assistant")
      || seenUserIds.has(assistant.userId)
    ) {
      return false;
    }
    seenUserIds.add(assistant.userId);
    return true;
  });
}

export const getAssistantRemovalUserId = (assistant: Assistant) => assistant.userId;

interface AssistantInvitation {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  assistantType: string;
  hourlyRate: number | null;
  status: string;
  expiresAt: string;
  createdAt: string;
}

function AssistantExpandableRow({ assistant, onRemove, isRemoving, onPermissions, isSavingPermissions }: {
  assistant: Assistant;
  onRemove: (id: string) => void;
  isRemoving: boolean;
  onPermissions: (permissions: Partial<Record<AssistantCapability, boolean>>) => void;
  isSavingPermissions: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Local permissions state so checkboxes respond instantly (no server round-trip lag).
  const [localPerms, setLocalPerms] = useState<Record<AssistantCapability, boolean>>(
    () => normalizePerms(assistant.permissions),
  );

  // Sync local state whenever the server sends back updated permissions (after a successful save).
  const permissionsKey = JSON.stringify(assistant.permissions);
  useEffect(() => {
    setLocalPerms(normalizePerms(assistant.permissions));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsKey]);

  const allEnabled = ALL_CAPABILITIES.every(k => localPerms[k]);

  function handleToggleAll() {
    const next = normalizePerms(
      allEnabled ? {} : Object.fromEntries(ALL_CAPABILITIES.map(k => [k, true])),
    );
    setLocalPerms(next);
    onPermissions(next);
  }

  function handleCapability(key: AssistantCapability, value: boolean) {
    const next = { ...localPerms, [key]: value };
    setLocalPerms(next);
    onPermissions(next);
  }

  const { data: sentGrants = [], isLoading: sharesLoading } = useSentResourceGrants();
  const shares = sentGrants.filter((grant) => grant.recipientUserId === assistant.userId);

  const getTypeBadge = (type: string) => {
    if (type === "virtual_assistant") {
      return (
        <Badge variant="secondary" data-testid={`badge-type-${type}`}>
          <Clock className="w-3 h-3 mr-1" />
          VA
        </Badge>
      );
    }
    if (type === "coach") return (
      <Badge variant="default" data-testid={`badge-type-${type}`}><User className="w-3 h-3 mr-1" />Coach</Badge>
    );
    return (
      <Badge variant="outline" data-testid={`badge-type-${type}`}>
        <User className="w-3 h-3 mr-1" />
        Assistant
      </Badge>
    );
  };

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <div
        className="border rounded-lg overflow-hidden"
        data-testid={`assistant-card-${assistant.id}`}
      >
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="flex-shrink-0">
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium" data-testid={`assistant-name-${assistant.id}`}>
                    {assistant.user.firstName && assistant.user.lastName
                      ? `${assistant.user.firstName} ${assistant.user.lastName}`
                      : assistant.user.email}
                  </span>
                  {getTypeBadge(assistant.assistantType)}
                </div>
                <p className="text-sm text-muted-foreground" data-testid={`assistant-email-${assistant.id}`}>
                  {assistant.user.email}
                </p>
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                {assistant.hourlyRate !== null && assistant.hourlyRate !== undefined && (
                  <div className="text-sm text-muted-foreground" data-testid={`assistant-rate-${assistant.id}`}>
                    <DollarSign className="w-4 h-4 inline mr-1" />
                    ${assistant.hourlyRate}/hr
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    data-testid={`button-remove-assistant-${assistant.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="dialog-confirm-remove">
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Remove {assistant.assistantType === "coach" ? "Coach" : "Assistant"}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to remove{" "}
                      <strong>{assistant.user.firstName || assistant.user.email}</strong> as an assistant?
                      {" "}Their relationship with you will end. Access to individually shared records and SOPs will end immediately.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="button-cancel-remove">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onRemove(getAssistantRemovalUserId(assistant))}
                      className="bg-red-600 hover:bg-red-700"
                      data-testid="button-confirm-remove"
                      disabled={isRemoving}
                    >
                      {isRemoving ? "Removing..." : "Remove"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="border-t bg-muted/20 p-4">
            <div className="mb-5 rounded-lg border bg-background p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div><p className="font-medium">Capabilities</p><p className="text-xs text-muted-foreground">Enabling a capability lets your assistant see and act on your data in that module. Practice modules (Planner, Intentions, Gratefuls, etc.) give access to all your records. Work modules (Goals, Tasks, SOPs, and Drive files) still require individual record sharing.</p></div>
                <Button size="sm" variant="outline" disabled={isSavingPermissions} onClick={handleToggleAll} className="flex-shrink-0 text-xs">
                  {allEnabled ? "Disable all" : "Enable all"}
                </Button>
              </div>
              {capabilityGroups.map(group => <div key={group.title}><p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{group.title}</p><div className="grid gap-2 sm:grid-cols-2">
                {group.items.map(([key, label]) => <label key={key} className="flex items-center justify-between rounded border px-3 py-2 text-sm"><span>{label}</span><input aria-label={`${label} access`} type="checkbox" checked={localPerms[key]} disabled={isSavingPermissions} onChange={event => handleCapability(key, event.target.checked)} /></label>)}
              </div></div>)}
            </div>
            {sharesLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : (
              <div className="space-y-2" data-testid={`assistant-individual-shares-${assistant.id}`}>
                <p className="text-xs text-muted-foreground">Only records shared individually through resource grants are listed here.</p>
                {shares.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No records shared with this assistant.</p>
                ) : shares.map((item) => (
                  <div key={item.id} className="flex items-center gap-2 text-sm p-2 bg-muted/50 rounded" data-testid={`assistant-share-${item.id}`}>
                    <FolderOpen className="w-3 h-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{item.resourceType}</span>
                    <span className="flex-1 truncate">{item.resourceTitle || "Untitled"}</span>
                    <Badge variant="outline" className="text-xs">{item.permission}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}interface AssistantsPageProps {
  rosterType?: AssistantRosterType;
}

export default function AssistantsPage({ rosterType = "assistants" }: AssistantsPageProps) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { data: allAssistants = [], isLoading: assistantsLoading } = useQuery<Assistant[]>({
    queryKey: ["/api/assistants"],
  });
  const assistants = filterAssistantRoster(allAssistants, rosterType);
  const isCoachRoster = rosterType === "coach";

  const [groupBy, setGroupBy] = useState<"none" | "type">("none");

  const assistantGroups = useMemo(() => {
    if (groupBy === "none") return [{ key: "", label: "", items: assistants }];
    const map = new Map<string, Assistant[]>();
    for (const a of assistants) {
      const label = a.assistantType === "coach" ? "Coach"
        : a.assistantType === "virtual_assistant" ? "Virtual Assistant"
        : "Assistant";
      if (!map.has(label)) map.set(label, []);
      map.get(label)!.push(a);
    }
    const order = ["Coach", "Virtual Assistant", "Assistant"];
    return Array.from(map.entries())
      .sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]))
      .map(([key, items]) => ({ key, label: key, items }));
  }, [assistants, groupBy]);

  const { data: invitations = [], isLoading: invitationsLoading } = useQuery<AssistantInvitation[]>({
    queryKey: ["/api/assistants/invitations"],
  });

  const pendingInvitations = invitations.filter(inv =>
    inv.status === "pending" &&
    (isCoachRoster ? inv.assistantType === "coach" : inv.assistantType !== "coach")
  );

  const removeAssistantMutation = useMutation({
    mutationFn: (userId: string) =>
      apiRequest("DELETE", `/api/assistants/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/assistants"] });
      toast({
        title: "Assistant removed",
        description: "The assistant has been removed successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to remove assistant",
        description: error?.message || "An error occurred while removing the assistant.",
        variant: "destructive",
      });
    },
  });
  const updatePermissionsMutation = useMutation({
    mutationFn: async ({ userId, permissions }: { userId: string; permissions: Partial<Record<AssistantCapability, boolean>> }) => {
      const response = await apiRequest("PATCH", `/api/assistants/${userId}`, { permissions });
      return response.json();
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["/api/assistants"] });
    },
    onSuccess: async () => {
      // Keep the controls pending until the authoritative permissions arrive.
      // Otherwise a second full-snapshot edit can use stale roster data.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["/api/assistants"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/assistants/effective-permissions"] }),
      ]);
      toast({ title: "Capabilities updated", description: "The assistant's access was updated immediately." });
    },
    onError: (error: any) => toast({ title: "Could not update capabilities", description: error?.message || "Please try again.", variant: "destructive" }),
  });

  const cancelInvitationMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/assistants/invitations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/assistants/invitations"] });
      toast({
        title: "Invitation cancelled",
        description: "The invitation has been cancelled successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to cancel invitation",
        description: error?.message || "An error occurred while cancelling the invitation.",
        variant: "destructive",
      });
    },
  });

  const resendInvitationMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/assistants/invitations/${id}/resend`);
      return res.json();
    },
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/assistants/invitations"] });
      if (result?.emailSent === false) {
        toast({
          title: "Email could not be delivered",
          description: "A new invitation link was created, but the email failed to send. The old link no longer works — please try resending again.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Invitation resent",
          description: "A fresh invitation link has been emailed. The old link no longer works.",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Failed to resend invitation",
        description: error?.message || "An error occurred while resending the invitation.",
        variant: "destructive",
      });
    },
  });

  const getTypeBadge = (type: string) => {
    if (type === "virtual_assistant") {
      return (
        <Badge variant="secondary" data-testid={`badge-type-${type}`}>
          <Clock className="w-3 h-3 mr-1" />
          VA
        </Badge>
      );
    }
    if (type === "coach") return <Badge variant="default" data-testid={`badge-type-${type}`}><User className="w-3 h-3 mr-1" />Coach</Badge>;
    return (
      <Badge variant="outline" data-testid={`badge-type-${type}`}>
        <User className="w-3 h-3 mr-1" />
        Assistant
      </Badge>
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="secondary" data-testid={`badge-status-${status}`}>Pending</Badge>;
      case "accepted":
        return <Badge variant="default" data-testid={`badge-status-${status}`}>Accepted</Badge>;
      case "expired":
        return <Badge variant="destructive" data-testid={`badge-status-${status}`}>Expired</Badge>;
      case "cancelled":
        return <Badge variant="outline" data-testid={`badge-status-${status}`}>Cancelled</Badge>;
      default:
        return <Badge variant="secondary" data-testid={`badge-status-${status}`}>{status}</Badge>;
    }
  };

  const rosterCard = (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle>{isCoachRoster ? "Active Coaches" : "Active Assistants"}</CardTitle>
            <CardDescription>
              Click on {isCoachRoster ? "a coach" : "an assistant"} to view records shared with them individually
            </CardDescription>
          </div>
          {assistants.length > 0 && (
            <Select value={groupBy} onValueChange={v => setGroupBy(v as "none" | "type")}>
              <SelectTrigger className="w-[150px] flex-shrink-0" data-testid="select-group-by-assistant">
                <SelectValue placeholder="Group by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No grouping</SelectItem>
                <SelectItem value="type">By type</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {assistantsLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-[200px]" />
                  <Skeleton className="h-3 w-[150px]" />
                </div>
              </div>
            ))}
          </div>
        ) : assistants.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground" data-testid={isCoachRoster ? "empty-coaches" : "empty-assistants"}>
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{isCoachRoster ? "No active coaches yet" : "No assistants yet"}</p>
            <p className="text-sm">Invite someone, then share only the specific records they need.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {assistantGroups.map(group => (
              <div key={group.key || "__all"}>
                {group.label && (
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    {group.label} ({group.items.length})
                  </p>
                )}
                <div className="space-y-3">
                  {group.items.map((assistant) => (
                    <AssistantExpandableRow
                      key={assistant.id}
                      assistant={assistant}
                      onRemove={(id) => removeAssistantMutation.mutate(id)}
                      isRemoving={removeAssistantMutation.isPending}
                      onPermissions={(permissions) => updatePermissionsMutation.mutate({ userId: assistant.userId, permissions })}
                      isSavingPermissions={updatePermissionsMutation.isPending}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="p-4 md:p-6 w-full max-w-full space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" data-testid="page-title">{isCoachRoster ? "Coach" : "Assistants"}</h1>
          <p className="text-muted-foreground">
             Manage {isCoachRoster ? "coach" : "assistant"} relationships and individually shared records
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isCoachRoster && assistants.length > 0 && (
            <Link href="/time-reports">
              <Button variant="outline" data-testid="button-time-reports">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Time Reports
              </Button>
            </Link>
          )}
          <Button
            data-testid={isCoachRoster ? "button-invite-coach" : "button-invite-assistant"}
            onClick={() => setLocation(`/communicate?invite=${isCoachRoster ? "coach" : "assistant"}`)}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {isCoachRoster ? "Invite Coach" : "Invite Assistant"}
          </Button>
      </div>
    </div>

      <Tabs defaultValue={isCoachRoster ? "coaches" : "assistants"} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value={isCoachRoster ? "coaches" : "assistants"} data-testid={isCoachRoster ? "tab-coaches" : "tab-assistants"}>
            <Users className="w-4 h-4 mr-2" />
            {isCoachRoster ? `Active Coaches (${assistants.length})` : `Assistants (${assistants.length})`}
          </TabsTrigger>
          <TabsTrigger value="invitations" data-testid="tab-invitations">
            <Mail className="w-4 h-4 mr-2" />
            Invited ({pendingInvitations.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={isCoachRoster ? "coaches" : "assistants"} className="space-y-4">
          {rosterCard}
        </TabsContent>

        <TabsContent value="invitations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Pending Invitations</CardTitle>
              <CardDescription>
                {isCoachRoster ? "Coach invitations" : "Assistant invitations"} that haven't been accepted yet
              </CardDescription>
            </CardHeader>
            <CardContent>
              {invitationsLoading ? (
                <div className="space-y-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="flex items-center gap-4">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div className="space-y-2 flex-1">
                        <Skeleton className="h-4 w-[200px]" />
                        <Skeleton className="h-3 w-[150px]" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : pendingInvitations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground" data-testid="empty-invitations">
                  <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No pending invitations</p>
                  <p className="text-sm">All invitations have been processed</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingInvitations.map((invitation) => (
                    <div
                      key={invitation.id}
                      className="flex items-start md:items-center justify-between p-4 border rounded-lg gap-4 flex-col md:flex-row"
                      data-testid={`invitation-card-${invitation.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium" data-testid={`invitation-email-${invitation.id}`}>
                            {invitation.email}
                          </span>
                          {getTypeBadge(invitation.assistantType)}
                          {getStatusBadge(invitation.status)}
                        </div>
                        {(invitation.firstName || invitation.lastName) && (
                          <p className="text-sm text-muted-foreground" data-testid={`invitation-name-${invitation.id}`}>
                            {[invitation.firstName, invitation.lastName].filter(Boolean).join(" ")}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2" data-testid={`invitation-access-${invitation.id}`}>
                          No blanket access to Notes/Notepad, Goals, Calendar, Tasks, Habits, or Tracker-X. Records must be shared individually.
                        </p>
                        <p className="text-xs text-muted-foreground mt-2" data-testid={`invitation-expires-${invitation.id}`}>
                          Expires: {new Date(invitation.expiresAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => resendInvitationMutation.mutate(invitation.id)}
                          disabled={resendInvitationMutation.isPending}
                          data-testid={`button-resend-invitation-${invitation.id}`}
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          Resend
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              data-testid={`button-cancel-invitation-${invitation.id}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Cancel
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent data-testid="dialog-confirm-cancel">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Cancel Invitation</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to cancel the invitation to{" "}
                                <strong>{invitation.email}</strong>?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid="button-cancel-cancel">Keep Invitation</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => cancelInvitationMutation.mutate(invitation.id)}
                                className="bg-red-600 hover:bg-red-700"
                                data-testid="button-confirm-cancel"
                              >
                                Cancel Invitation
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export function CoachPage() {
  return <AssistantsPage rosterType="coach" />;
}

const getAssistantName = (assistant: Assistant) =>
  [assistant.user.firstName, assistant.user.lastName].filter(Boolean).join(" ") || getAssistantEmail(assistant);

const getAssistantEmail = (assistant: Assistant) => assistant.user.email || "No email address";
